import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSearch } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money } from "./billingCommon";
import { Button } from "@/components/ui/button";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

export default function Statements() {
  const today = new Date().toISOString().slice(0, 10);
  const ninety = new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10);
  const [form, setForm] = useState({ email: "", fromDate: ninety, toDate: today });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const params = () => new URLSearchParams({ email: form.email, fromDate: form.fromDate, toDate: form.toDate }).toString();

  const preview = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Enter a customer email");
    setLoading(true);
    try {
      const res = await api.get(`/statements/preview?${params()}`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageTitle title="Statements" subtitle="Generate a branded account statement of charges and payments for a customer" />
      <form onSubmit={preview} className="rounded-xl bg-white border border-slate-200 p-5 grid gap-3 sm:grid-cols-4 items-end mb-6" data-testid="statement-form">
        <label className="text-sm text-slate-600 sm:col-span-2">Customer email
          <input className={input} type="email" required placeholder="customer@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="statement-email" />
        </label>
        <label className="text-sm text-slate-600">From
          <input className={input} type="date" value={form.fromDate} onChange={(e) => set("fromDate", e.target.value)} data-testid="statement-from" />
        </label>
        <label className="text-sm text-slate-600">To
          <input className={input} type="date" value={form.toDate} onChange={(e) => set("toDate", e.target.value)} data-testid="statement-to" />
        </label>
        <Button type="submit" disabled={loading} className="bg-electric hover:bg-tech gap-1.5 sm:col-span-4 sm:w-max" data-testid="statement-preview-btn">
          <FileSearch className="h-4 w-4" /> {loading ? "Loading…" : "Preview Statement"}
        </Button>
      </form>

      {data && (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="statement-result">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center flex-wrap gap-3">
            <div>
              <p className="font-heading font-semibold text-navy">{data.customerName}</p>
              <p className="text-xs text-slate-500">{data.customerEmail} · {fmtDate(data.fromDate)} — {fmtDate(data.toDate)}</p>
            </div>
            <a href={`${API}/statements/pdf?${params()}`} target="_blank" rel="noreferrer" data-testid="statement-pdf-btn">
              <Button variant="outline" className="gap-1.5"><FileDown className="h-4 w-4" /> Download PDF</Button>
            </a>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
              <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Description</th><th className="text-right p-3">Charges</th><th className="text-right p-3">Payments</th></tr>
            </thead>
            <tbody>
              {data.lines.length === 0 ? <tr><td colSpan={4} className="p-8 text-center text-slate-400">No activity in this period.</td></tr> :
                data.lines.map((ln, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-3 text-slate-500">{fmtDate(ln.date)}</td>
                    <td className="p-3 text-navy">{ln.description}</td>
                    <td className="p-3 text-right text-navy">{ln.charge ? money(ln.charge) : ""}</td>
                    <td className="p-3 text-right text-green-600">{ln.payment ? money(ln.payment) : ""}</td>
                  </tr>
                ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 font-medium">
              <tr><td className="p-3" colSpan={2}>Total Charges</td><td className="p-3 text-right text-navy">{money(data.totalCharges)}</td><td></td></tr>
              <tr><td className="p-3" colSpan={2}>Total Payments</td><td></td><td className="p-3 text-right text-green-600">{money(data.totalPaid)}</td></tr>
              <tr className="text-electric"><td className="p-3" colSpan={2}>Balance</td><td className="p-3 text-right" colSpan={2}>{money(data.balance)}</td></tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
